import React, { useState, useMemo } from 'react';
import { User, Users, Plus, Eye, Edit, Trash2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import type { User as UserType, UserMaintenanceState, PaginationInfo } from '../../../../types/userMaintenance';
import { mockUsers } from '../../../../data/mockUsers';
import UserDetailsModal from './components/UserDetailsModal';
import '../../../../styles/userMaintenance.css';

interface UserMaintenanceProps {
  className?: string;
  onEditUser?: (user: UserType) => void;
}

const UserMaintenance: React.FC<UserMaintenanceProps> = ({ className = '', onEditUser }) => {
  const [state, setState] = useState<UserMaintenanceState>({
    users: mockUsers,
    searchTerm: '',
    currentPage: 1,
    itemsPerPage: 5,
    totalUsers: mockUsers.length,
    selectedUsers: [],
    isLoading: false
  });

  const [viewDetailsModal, setViewDetailsModal] = useState<{
    isOpen: boolean;
    user: UserType | null;
  }>({ isOpen: false, user: null });

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!state.searchTerm) {
      return state.users;
    }

    const searchLower = state.searchTerm.toLowerCase();
    return state.users.filter(user =>
      user.username.toLowerCase().includes(searchLower) ||
      user.firstName.toLowerCase().includes(searchLower) ||
      user.middleName.toLowerCase().includes(searchLower) ||
      user.lastName.toLowerCase().includes(searchLower) ||
      (user.email && user.email.toLowerCase().includes(searchLower))
    );
  }, [state.users, state.searchTerm]);

  // Calculate pagination
  const paginationInfo = useMemo((): PaginationInfo => {
    const totalUsers = filteredUsers.length;
    const totalPages = Math.ceil(totalUsers / state.itemsPerPage);
    const startUser = (state.currentPage - 1) * state.itemsPerPage + 1;
    const endUser = Math.min(state.currentPage * state.itemsPerPage, totalUsers);

    return {
      currentPage: state.currentPage,
      totalPages,
      totalUsers,
      itemsPerPage: state.itemsPerPage,
      startUser: totalUsers > 0 ? startUser : 0,
      endUser: totalUsers > 0 ? endUser : 0
    };
  }, [filteredUsers.length, state.currentPage, state.itemsPerPage]);

  // Get current page users
  const currentPageUsers = useMemo(() => {
    const startIndex = (state.currentPage - 1) * state.itemsPerPage;
    const endIndex = startIndex + state.itemsPerPage;
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, state.currentPage, state.itemsPerPage]);

  const handleSearchChange = (searchTerm: string) => {
    setState(prev => ({
      ...prev,
      searchTerm,
      currentPage: 1 // Reset to first page when searching
    }));
  };

  const handlePageChange = (page: number) => {
    setState(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  const handleCreateNew = () => {
    alert('Create New User Modal\n\nThis would open a form with:\n- User details input\n- Role assignment\n- Permission configuration\n- Account activation options');
  };

  const handleViewUser = (user: UserType) => {
    setViewDetailsModal({ isOpen: true, user });
  };

  const handleEditUser = (user: UserType) => {
    if (onEditUser) {
      onEditUser(user);
    } else {
      alert(`Edit User Modal\n\nThis would open an edit form for:\n- User: ${user.firstName} ${user.lastName}\n- Username: ${user.username}\n- Role modifications\n- Permission updates`);
    }
  };

  const handleDeleteUser = (user: UserType) => {
    const confirmDelete = confirm(`Are you sure you want to delete user "${user.firstName} ${user.lastName}"?\n\nThis action cannot be undone.`);
    if (confirmDelete) {
      setState(prev => ({
        ...prev,
        users: prev.users.filter(u => u.id !== user.id),
        totalUsers: prev.totalUsers - 1
      }));
      alert(`User "${user.firstName} ${user.lastName}" has been deleted successfully.`);
    }
  };

  const renderPagination = () => {
    return (
      <div className="user-pagination">
        <div className="pagination-info">
          Showing {paginationInfo.startUser} to {paginationInfo.endUser} of {paginationInfo.totalUsers} users
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(paginationInfo.currentPage - 1)}
            disabled={paginationInfo.currentPage === 1}
          >
            <ChevronLeft size={16} />
          </button>
          
          {Array.from({ length: paginationInfo.totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`pagination-number ${page === paginationInfo.currentPage ? 'active' : ''}`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}
          
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(paginationInfo.currentPage + 1)}
            disabled={paginationInfo.currentPage === paginationInfo.totalPages}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`user-maintenance ${className}`}>
      {/* Header */}
      <div className="user-maintenance-header">
        <div className="user-maintenance-header-left">
          <div className="user-maintenance-icon">
            <User size={24} />
          </div>
          <div className="user-maintenance-title-section">
            <h1 className="user-maintenance-title">Manage Users and Accounts</h1>
            <div className="user-maintenance-context">
              Create, edit, and manage user accounts and their permissions
            </div>
          </div>
        </div>
        
        <div className="user-maintenance-header-actions">
          <button
            className="btn-create-new-user"
            onClick={handleCreateNew}
          >
            <Plus size={16} />
            Create New
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="user-maintenance-search-section">
        <div className="user-search-controls">
          <div className="user-search-input-container">
            <Search size={16} className="user-search-icon" />
            <input
              type="text"
              placeholder="Search users by username, name, or any field..."
              value={state.searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="user-search-input"
            />
            <div className="user-search-tooltip">
              Search by username, first name, middle name, or last name. Type to filter results instantly.
            </div>
          </div>
        </div>
        
        <div className="user-count-info">
          <Users size={16} className="user-count-icon" />
          <span className="user-count-text">{filteredUsers.length} users found</span>
        </div>
      </div>

      {/* Table */}
      <div className="user-table-container">
        <table className="user-table">
          <thead className="user-table-header">
            <tr>
              <th className="username-column">USERNAME</th>
              <th className="name-column">FIRST NAME</th>
              <th className="name-column">MIDDLE NAME</th>
              <th className="name-column">LAST NAME</th>
              <th className="actions-column">ACTIONS</th>
            </tr>
          </thead>
          <tbody className="user-table-body">
            {currentPageUsers.map((user) => (
              <tr key={user.id} className="user-row">
                <td className="username-cell">{user.username}</td>
                <td className="name-cell">{user.firstName}</td>
                <td className="middle-name-cell">{user.middleName}</td>
                <td className="name-cell">{user.lastName}</td>
                <td>
                  <div className="user-action-buttons">
                    <button
                      className="user-action-btn view"
                      onClick={() => handleViewUser(user)}
                      title="View User"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      className="user-action-btn edit"
                      onClick={() => handleEditUser(user)}
                      title="Edit User"
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      className="user-action-btn delete"
                      onClick={() => handleDeleteUser(user)}
                      title="Delete User"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {renderPagination()}

      {/* User Details Modal */}
      <UserDetailsModal
        isOpen={viewDetailsModal.isOpen}
        onClose={() => setViewDetailsModal({ isOpen: false, user: null })}
        user={viewDetailsModal.user}
        onEdit={(user) => {
          setViewDetailsModal({ isOpen: false, user: null });
          handleEditUser(user);
        }}
      />
    </div>
  );
};

export default UserMaintenance;